import React from 'react'
import clsx from 'clsx'

import type { HTMLAttributes } from 'react'

import { IconArrowDown, IconMore } from 'components/Icons'
import { getLabel } from 'helpers/language'

import type { IColumn, ISorting } from 'types'

interface Props
  extends Exclude<HTMLAttributes<HTMLTableSectionElement>, 'children'> {
  columns: IColumn[]
  sorting?: ISorting
  onSortChange?: (sort: ISorting) => void
  onToggleChange?: () => void
  position?: string
}

const THead: React.VFC<Props> = ({
  className,
  columns,
  sorting,
  onSortChange = () => {},
  onToggleChange = () => {},
  position,
  ...rest
}: Props) => {
  const base = 'thead'
  const cn = clsx(base, className)

  const iconCn = (column: IColumn): string => {
    const shouldActive =
      sorting && sorting.column === column.key && sorting.order === 'DESC'

    return clsx(`${base}__icon`, shouldActive && `${base}__icon--active`)
  }

  const setSortingHandle = (column: IColumn) => {
    const isSameColumn = sorting && column.key === sorting.column
    const order = isSameColumn
      ? sorting?.order === 'ASC'
        ? 'DESC'
        : 'ASC'
      : 'DESC'

    const next: ISorting = { column: column.key, order }

    return onSortChange(next)
  }

  const checkboxColumn = (
    <th
      className={`${base}__th table__cell--w${columns[0].width}`}
      key={columns[0].key}
      onClick={onToggleChange}
      data-testid={`header-checkbox`}
    >
      <span className={`${base}__title ${base}__th--checkbox`}>
        <IconMore variants='sm' />
      </span>
    </th>
  )

  const header = (column: IColumn): JSX.Element => (
    <th
      className={clsx(
        `${base}__th`,
        `table__cell--w${column.width}`,
        column?.sortable && `${base}__th--sortable`,
      )}
      key={column.key}
      data-testid={`header-${column.key}`}
    >
      <div
        className={clsx(
          `${base}__title`,
          position === 'left' && `${base}__title--left`,
        )}
        {...(column.sortable && {
          onClick: () => setSortingHandle(column),
        })}
      >
        {column.label && getLabel(`${column.label}`)}
        {column?.sortable && (
          <IconArrowDown className={iconCn(column)} variants='xs' />
        )}
      </div>
    </th>
  )

  return (
    <thead className={cn} {...rest}>
      <tr>
        {columns.map((column) => {
          let headerTable: JSX.Element = header(column)

          if (columns[0].key === 'no') {
            headerTable = header(column)
          } else {
            headerTable =
              column.key === 'checkbox' ? checkboxColumn : header(column)
          }

          return headerTable
        })}
      </tr>
    </thead>
  )
}

export default THead
